class MBMTheme {
  constructor() {
    this.initCartDrawer();
    this.initProductForm();
    this.initGalleries();
    this.initRecommendations();
    this.initHomeScentCarousels();
    this.initCartQuantityControls();
  }

  initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    document.querySelectorAll('[data-cart-open]').forEach((button) => {
      button.addEventListener('click', () => {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        drawer.removeAttribute('inert');
      });
    });

    document.querySelectorAll('[data-cart-close]').forEach((button) => {
      button.addEventListener('click', () => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('inert', '');
      });
    });

    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('inert', '');
      }
    });
  }

  initGalleries() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    document.querySelectorAll('[data-gallery], [data-product-gallery]').forEach((gallery) => {
      const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      const mainImage = gallery.querySelector('[data-main-image], .story-main-image img');
      const currentPosition = gallery.querySelector('[data-gallery-current]');
      if (!mainImage || !thumbnails.length) return;

      const activateThumbnail = (thumbnail, pause = false) => {
        const src = thumbnail.dataset.imageSrc || thumbnail.dataset.image;
        if (!src) return;

        if (pause) gallery.dataset.galleryResumeAt = String(Date.now() + 8000);
        gallery.classList.add('is-changing');
        window.clearTimeout(gallery.galleryTransition);
        gallery.galleryTransition = window.setTimeout(() => {
          mainImage.src = src;
          mainImage.removeAttribute('srcset');
          mainImage.removeAttribute('sizes');
          if (thumbnail.dataset.imageAlt) mainImage.alt = thumbnail.dataset.imageAlt;

          thumbnails.forEach((button) => {
            button.classList.remove('is-active');
            button.setAttribute('aria-pressed', 'false');
          });
          thumbnail.classList.add('is-active');
          thumbnail.setAttribute('aria-pressed', 'true');
          const activeIndex = thumbnails.indexOf(thumbnail);
          if (currentPosition) currentPosition.textContent = String(activeIndex + 1);

          window.requestAnimationFrame(() => gallery.classList.remove('is-changing'));
        }, 140);
      };

      const moveGallery = (direction, pause = false) => {
        const activeIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-active')));
        const nextIndex = (activeIndex + direction + thumbnails.length) % thumbnails.length;
        activateThumbnail(thumbnails[nextIndex], pause);
      };

      const initialIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('is-active')));
      if (currentPosition) currentPosition.textContent = String(initialIndex + 1);

      thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => activateThumbnail(thumbnail, true));
      });

      gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => moveGallery(-1, true));
      gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => moveGallery(1, true));

      gallery.addEventListener('mouseenter', () => {
        gallery.dataset.galleryHover = 'true';
      });
      gallery.addEventListener('mouseleave', () => {
        delete gallery.dataset.galleryHover;
      });

      if (gallery.hasAttribute('data-gallery-auto') && thumbnails.length > 1 && !reduceMotion) {
        window.setInterval(() => {
          if (Number(gallery.dataset.galleryResumeAt || 0) > Date.now() || gallery.dataset.galleryHover || document.hidden) return;
          const bounds = gallery.getBoundingClientRect();
          if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;
          moveGallery(1);
        }, 4000);
      }
    });
  }

  initRecommendations(scope = document) {
    scope.querySelectorAll('[data-recommendations]').forEach((carousel) => {
      if (carousel.dataset.recommendationsInitialized === 'true') return;

      const track = carousel.querySelector('[data-recommendations-track]');
      const cards = Array.from(track?.querySelectorAll('.recommendation-card') || []);
      if (!track || cards.length < 2) return;

      carousel.dataset.recommendationsInitialized = 'true';
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      cards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.dataset.recommendationClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        if (clone.matches('a, button, input, select, textarea')) clone.tabIndex = -1;
        clone.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          element.tabIndex = -1;
        });
        track.appendChild(clone);
      });

      const allCards = Array.from(track.querySelectorAll('.recommendation-card'));
      let hoverPaused = false;
      let focusPaused = false;
      let pointerActive = false;
      let lastFrame = performance.now();
      const pixelsPerMillisecond = reduceMotion ? 0.01 : 0.022;

      carousel.addEventListener('mouseenter', () => { hoverPaused = true; });
      carousel.addEventListener('mouseleave', () => { hoverPaused = false; });
      carousel.addEventListener('focusin', () => { focusPaused = true; });
      carousel.addEventListener('focusout', (event) => {
        if (!carousel.contains(event.relatedTarget)) focusPaused = false;
      });

      track.addEventListener('pointerdown', () => { pointerActive = true; });
      track.addEventListener('pointerup', () => { pointerActive = false; });
      track.addEventListener('pointercancel', () => { pointerActive = false; });
      window.addEventListener('pointerup', () => { pointerActive = false; });
      window.addEventListener('pointercancel', () => { pointerActive = false; });
      track.addEventListener('pointerleave', (event) => {
        if (event.buttons === 0) pointerActive = false;
      });

      const animateRecommendations = (timestamp) => {
        if (!carousel.isConnected) {
          return;
        }

        const elapsed = Math.min(timestamp - lastFrame, 64);
        lastFrame = timestamp;
        const bounds = carousel.getBoundingClientRect();
        const isVisible = bounds.bottom > 0 && bounds.top < window.innerHeight;

        if (!hoverPaused && !focusPaused && !pointerActive && !document.hidden && isVisible) {
          track.scrollLeft += elapsed * pixelsPerMillisecond;
          const firstClone = allCards[cards.length];
          const loopWidth = firstClone ? firstClone.offsetLeft - cards[0].offsetLeft : 0;
          if (loopWidth > 0 && track.scrollLeft >= loopWidth) {
            track.scrollLeft -= loopWidth;
          }
        }

        carousel.recommendationsFrame = window.requestAnimationFrame(animateRecommendations);
      };

      carousel.recommendationsFrame = window.requestAnimationFrame(animateRecommendations);
    });
  }

  initHomeScentCarousels(scope = document) {
    scope.querySelectorAll('[data-home-scent-carousel]').forEach((carousel) => {
      if (carousel.dataset.homeScentInitialized === 'true') return;

      const viewport = carousel.querySelector('[data-home-scent-viewport]');
      const track = carousel.querySelector('[data-home-scent-track]');
      const items = Array.from(carousel.querySelectorAll('[data-home-scent-item]'));
      const currentPosition = carousel.querySelector('[data-home-scent-current]');
      const mobileQuery = window.matchMedia('(max-width: 700px)');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!viewport || !track || items.length < 2) return;

      carousel.dataset.homeScentInitialized = 'true';

      const step = 360 / items.length;
      let activeIndex = 0;
      let rotation = 0;
      let resumeAt = 0;
      let pointerStart = 0;
      let mobilePointerStart = 0;
      let rotationStart = 0;
      let pointerDown = false;
      let dragged = false;
      let mobileDragged = false;
      let scrollTimer = null;
      let hoverPaused = false;
      let focusPaused = false;

      items.forEach((item, index) => {
        item.style.setProperty('--scent-angle', `${index * step}deg`);
      });

      const normalizedIndex = (index) => (index + items.length) % items.length;

      const updateState = () => {
        items.forEach((item, index) => {
          const isActive = index === activeIndex;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
        if (currentPosition) currentPosition.textContent = String(activeIndex + 1).padStart(2, '0');
      };

      const setOrbitRotation = (nextRotation) => {
        rotation = nextRotation;
        carousel.style.setProperty('--carousel-rotation', `${rotation}deg`);
        items.forEach((item, index) => {
          item.style.setProperty('--card-rotation', `${-(rotation + index * step)}deg`);
        });
      };

      const centerMobileItem = (behavior = 'smooth') => {
        const item = items[activeIndex];
        const left = item.offsetLeft - (track.clientWidth - item.offsetWidth) / 2;
        track.scrollTo({ left, behavior });
      };

      const goTo = (nextIndex, behavior = 'smooth', direction = 0) => {
        activeIndex = normalizedIndex(nextIndex);
        if (mobileQuery.matches) {
          centerMobileItem(behavior);
        } else if (direction) {
          setOrbitRotation(rotation - direction * step);
        } else {
          setOrbitRotation(-activeIndex * step);
        }
        updateState();
      };

      const pauseAfterInteraction = () => {
        resumeAt = Date.now() + 7200;
      };

      carousel.querySelector('[data-home-scent-prev]')?.addEventListener('click', () => {
        pauseAfterInteraction();
        goTo(activeIndex - 1, 'smooth', -1);
      });
      carousel.querySelector('[data-home-scent-next]')?.addEventListener('click', () => {
        pauseAfterInteraction();
        goTo(activeIndex + 1, 'smooth', 1);
      });

      carousel.addEventListener('mouseenter', () => { hoverPaused = true; });
      carousel.addEventListener('mouseleave', () => {
        hoverPaused = false;
        resumeAt = 0;
      });
      carousel.addEventListener('focusin', () => { focusPaused = true; });
      carousel.addEventListener('focusout', (event) => {
        if (!carousel.contains(event.relatedTarget)) {
          focusPaused = false;
          resumeAt = 0;
        }
      });

      viewport.addEventListener('pointerdown', (event) => {
        if (mobileQuery.matches || event.button !== 0) return;
        pointerStart = event.clientX;
        rotationStart = rotation;
        pointerDown = true;
        dragged = false;
        pauseAfterInteraction();
      });

      viewport.addEventListener('pointermove', (event) => {
        if (mobileQuery.matches || !pointerDown) return;
        const distance = event.clientX - pointerStart;
        if (!viewport.hasPointerCapture(event.pointerId)) {
          if (Math.abs(distance) <= 8) return;
          dragged = true;
          viewport.setPointerCapture(event.pointerId);
          carousel.classList.add('is-dragging');
        }
        setOrbitRotation(rotationStart + distance * 0.13);
      });

      const finishDrag = (event) => {
        if (mobileQuery.matches) return;
        pointerDown = false;
        if (!viewport.hasPointerCapture(event.pointerId)) return;
        viewport.releasePointerCapture(event.pointerId);
        carousel.classList.remove('is-dragging');
        const snappedStep = Math.round(rotation / step);
        activeIndex = normalizedIndex(-snappedStep);
        setOrbitRotation(snappedStep * step);
        updateState();
      };

      const clearDragStateAfterClick = () => {
        window.setTimeout(() => {
          dragged = false;
          mobileDragged = false;
        }, 0);
      };

      viewport.addEventListener('pointerup', (event) => {
        finishDrag(event);
        clearDragStateAfterClick();
      });
      viewport.addEventListener('pointercancel', (event) => {
        finishDrag(event);
        clearDragStateAfterClick();
      });
      carousel.addEventListener('click', (event) => {
        if (!dragged && !mobileDragged) return;
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
        mobileDragged = false;
      }, true);

      track.addEventListener('pointerdown', (event) => {
        if (!mobileQuery.matches) return;
        mobilePointerStart = event.clientX;
        mobileDragged = false;
        pauseAfterInteraction();
      });
      track.addEventListener('pointermove', (event) => {
        if (mobileQuery.matches && Math.abs(event.clientX - mobilePointerStart) > 8) mobileDragged = true;
      });
      track.addEventListener('pointerup', clearDragStateAfterClick);
      track.addEventListener('pointercancel', clearDragStateAfterClick);
      track.addEventListener('wheel', () => {
        if (mobileQuery.matches) pauseAfterInteraction();
      }, { passive: true });

      track.addEventListener('scroll', () => {
        if (!mobileQuery.matches) return;
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          const center = track.scrollLeft + track.clientWidth / 2;
          activeIndex = items.reduce((closest, item, index) =>
            Math.abs(item.offsetLeft + item.offsetWidth / 2 - center) < Math.abs(items[closest].offsetLeft + items[closest].offsetWidth / 2 - center)
              ? index
              : closest
          , 0);
          updateState();
        }, 90);
      }, { passive: true });

      mobileQuery.addEventListener('change', () => {
        setOrbitRotation(-activeIndex * step);
        if (mobileQuery.matches) centerMobileItem('auto');
        updateState();
      });

      setOrbitRotation(0);
      updateState();

      carousel.homeScentTimer = window.setInterval(() => {
        if (!carousel.isConnected) {
          window.clearInterval(carousel.homeScentTimer);
          return;
        }
        if (Date.now() < resumeAt || document.hidden || hoverPaused || focusPaused) return;
        const bounds = carousel.getBoundingClientRect();
        if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;
        goTo(activeIndex + 1, reduceMotion ? 'auto' : 'smooth', 1);
      }, 6400);
    });
  }

  initProductForm() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const productScript = root.closest('.product-page')?.querySelector('[data-product-json]');
      let product = null;
      try {
        product = JSON.parse(productScript?.textContent || '');
      } catch (error) {}

      const form = root.querySelector('[data-product-form]');
      if (!form || !product) return;

      const idInput = form.querySelector('[data-variant-id]');
      const price = form.querySelector('[data-price]');
      const status = form.querySelector('[data-variant-status]');
      const addButtons = Array.from(root.querySelectorAll('[data-add-to-cart], button[type="submit"][name="add"], button.sticky-atc[form]'));
      const selectedStyle = form.querySelector('[data-selected-style]');
      const includedStyle = root.querySelector('[data-included-style]');
      const swatches = Array.from(form.querySelectorAll('.format-swatch'));

      const getSelectedOptions = () => Array.from(form.querySelectorAll('.format-swatch.is-selected'))
        .sort((first, second) => Number(first.dataset.optionPosition) - Number(second.dataset.optionPosition))
        .map((selected) => selected.dataset.optionValue);

      const findVariant = (selectedOptions) => product.variants.find((candidate) =>
        candidate.options.every((option, index) => option === selectedOptions[index])
      );

      const updateAddButtons = (isAvailable, text = isAvailable ? 'Add to Cart' : 'Sold Out') => {
        addButtons.forEach((button) => {
          button.disabled = !isAvailable;
          button.textContent = text;
          if (isAvailable) button.removeAttribute('aria-disabled');
          else button.setAttribute('aria-disabled', 'true');
        });
      };

      const updatePurchaseState = (variant) => {
        const isAvailable = Boolean(variant?.available);
        if (variant) {
          idInput.value = variant.id;
          if (price) price.textContent = this.formatMoney(variant.price);
          if (selectedStyle) selectedStyle.textContent = variant.title;
          if (includedStyle) includedStyle.textContent = variant.title;
        }
        if (status) status.hidden = isAvailable;
        updateAddButtons(isAvailable);
      };

      const updateOptionAvailability = () => {
        const selectedOptions = getSelectedOptions();
        swatches.forEach((button) => {
          const candidateOptions = [...selectedOptions];
          candidateOptions[Number(button.dataset.optionPosition) - 1] = button.dataset.optionValue;
          const candidate = findVariant(candidateOptions);
          const isUnavailable = !candidate || !candidate.available;
          button.disabled = isUnavailable;
          button.classList.toggle('is-sold-out', isUnavailable);
          button.setAttribute('aria-disabled', String(isUnavailable));
          const optionStatus = button.querySelector('[data-option-status]');
          if (optionStatus) optionStatus.hidden = !isUnavailable;
        });
      };

      swatches.forEach((button) => {
        button.addEventListener('click', () => {
          const fieldset = button.closest('.variant-fieldset');
          fieldset.querySelectorAll('.format-swatch').forEach((optionButton) => {
            optionButton.classList.remove('is-selected');
            optionButton.setAttribute('aria-pressed', 'false');
          });

          button.classList.add('is-selected');
          button.setAttribute('aria-pressed', 'true');

          const variant = findVariant(getSelectedOptions());
          updatePurchaseState(variant);
          updateOptionAvailability();
          if (!variant) return;

          const variantImage = variant.featured_image?.src || variant.featured_image?.url;
          const mainImage = root.querySelector('[data-main-image]');
          if (mainImage && variantImage) {
            mainImage.src = variantImage;
            mainImage.removeAttribute('srcset');
            mainImage.removeAttribute('sizes');
          }

          const nextUrl = new URL(window.location.href);
          nextUrl.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', nextUrl);
        });
      });

      const initialVariant = product.variants.find((variant) => String(variant.id) === String(idInput.value));
      updatePurchaseState(initialVariant);
      updateOptionAvailability();

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const selectedVariant = product.variants.find((variant) => String(variant.id) === String(idInput.value));
        if (!selectedVariant?.available) {
          updatePurchaseState(selectedVariant);
          return;
        }
        const formData = new FormData(form);
        updateAddButtons(false, 'Adding…');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });
          if (!response.ok) throw new Error('Unable to add item');
          await this.refreshCart();
          document.querySelector('[data-cart-open]')?.click();
        } catch (error) {
          form.submit();
        } finally {
        const selectedVariant = product.variants.find((variant) => String(variant.id) === String(idInput.value));
        const isAvailable = Boolean(selectedVariant?.available);
        updateAddButtons(isAvailable);
      }
      });
    });
  }

  initCartQuantityControls() {
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-cart-increase], [data-cart-decrease]');
      if (!button) return;

      const control = button.closest('[data-quantity-control]');
      if (!control || control.dataset.loading) return;

      const currentQuantity = Number(control.dataset.quantity || 0);
      const nextQuantity = button.hasAttribute('data-cart-increase') ? currentQuantity + 1 : Math.max(0, currentQuantity - 1);
      control.dataset.loading = 'true';
      control.querySelectorAll('button').forEach((controlButton) => { controlButton.disabled = true; });

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: control.dataset.lineKey, quantity: nextQuantity }),
        });
        if (!response.ok) throw new Error('Unable to update cart');
        this.renderCart(await response.json());
      } catch (error) {
        delete control.dataset.loading;
        control.querySelectorAll('button').forEach((controlButton) => { controlButton.disabled = false; });
      }
    });
  }

  async refreshCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to refresh cart');
    this.renderCart(await response.json());
  }

  renderCart(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
      element.hidden = cart.item_count === 0;
    });

    document.querySelectorAll('[data-cart-subtotal]').forEach((element) => {
      element.textContent = `Subtotal: ${this.formatMoney(cart.total_price)}`;
    });

    const drawerBody = document.querySelector('[data-cart-drawer-body]');
    if (drawerBody) {
      drawerBody.innerHTML = cart.items.length
        ? cart.items.map((item) => this.cartItemMarkup(item, true)).join('')
        : '<p>Your cart is empty.</p>';
    }

    const pageLines = document.querySelector('[data-cart-page-lines]');
    if (pageLines) {
      pageLines.innerHTML = cart.items.length
        ? cart.items.map((item) => this.cartItemMarkup(item, false)).join('')
        : '<p>Your cart is empty.</p><a class="button" href="/collections/atmosphere">Choose your atmosphere</a>';
    }

    const checkoutButton = document.querySelector('[data-cart-page] [name="checkout"]');
    if (checkoutButton) checkoutButton.hidden = cart.item_count === 0;
  }

  cartItemMarkup(item, compact) {
    const title = this.escapeHtml(item.product_title);
    const url = this.escapeHtml(item.url || '#');
    const image = item.image
      ? `<img src="${this.escapeHtml(item.image)}" alt="${title}" loading="lazy">`
      : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<small>${this.escapeHtml(item.variant_title)}</small>`
      : '';
    const quantity = `
      <div class="quantity-control" data-quantity-control data-line-key="${this.escapeHtml(item.key)}" data-quantity="${item.quantity}">
        <button type="button" data-cart-decrease aria-label="Decrease ${title} quantity">−</button>
        <span aria-live="polite">${item.quantity}</span>
        <button type="button" data-cart-increase aria-label="Increase ${title} quantity">+</button>
      </div>`;

    return compact
      ? `<div class="drawer-line" data-cart-item><a class="cart-item-image" href="${url}">${image}</a><div class="cart-item-meta"><a href="${url}">${title}</a>${variant}${quantity}</div><strong>${this.formatMoney(item.final_line_price)}</strong></div>`
      : `<div class="cart-line" data-cart-item><a class="cart-item-image" href="${url}">${image}</a><div class="cart-item-meta"><a href="${url}">${title}</a>${variant}</div>${quantity}<strong>${this.formatMoney(item.final_line_price)}</strong></div>`;
  }

  escapeHtml(value) {
    const characters = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(value ?? '').replace(/[&<>"']/g, (character) => characters[character]);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'CAD',
    }).format(cents / 100);
  }
}

const initializeMBMTheme = () => {
  if (!window.mbmTheme) window.mbmTheme = new MBMTheme();
  return window.mbmTheme;
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMBMTheme, { once: true });
} else {
  initializeMBMTheme();
}

document.addEventListener('shopify:section:load', (event) => {
  const theme = initializeMBMTheme();
  theme.initRecommendations(event.target);
  theme.initHomeScentCarousels(event.target);
});
